import React, { useEffect } from "react"
import { Box, Chip, FormHelperText, Typography } from "@mui/material"
import { DateRangePickerSeeker } from "@ui-seeker/core"
import { batch, useSignal } from "@preact/signals-react"
import { formatDate } from "@ui-seeker/utils"
import { parseBackendDate } from "@utils"
import { dsRepricingDrawerData, dsRepricingDrawerErrors } from "Pages/DsRepricing/Page/@signals"
import { readPeriod, withPeriodField } from "Pages/DsRepricing/Page/@components/periodShape"
import {
	rangeAddRowSx, rangeChipArrowSx, rangeChipFiguresSx, rangeChipSx, rangeWellSx,
} from "Pages/DsRepricing/Page/@components/rangeSx"

// Seasons keep the DateRangePicker (autocommit on range pick) instead of two
// separate date inputs — a single range picker is the calendar UI most users
// already have muscle memory for. The visual container mirrors RangeList so
// the wizard reads the same across all three period modes.
/**
 * Do two `MM-DD` ranges share a day?
 *
 * A season may wrap the new year (12-15 → 01-10), in which case `from`
 * sorts after `to` and a plain string comparison says the wrong thing.
 * The server splits a wrapping range in two before comparing
 * (`rules.py:_expand_wrapping`); this does the same, so the warning
 * shown here and the rejection sent back mean the same thing.
 */
function expandWrapping(from, to) {
	return from <= to ? [[from, to]] : [[from, '12-31'], ['01-01', to]]
}

function overlapsSeason(from, to, existing) {
	const mine = expandWrapping(from, to)
	const theirs = expandWrapping(existing.seasonFrom, existing.seasonTo)
	return mine.some(([a, b]) => theirs.some(([c, d]) => a <= d && c <= b))
}

export default function DsRepricingSeasons({ active = true }) {
	const seasons = readPeriod(dsRepricingDrawerData.value).seasons
	const pickerValue = useSignal(null)
	const overlapError = useSignal(null)
	const isValid = !active || seasons.length > 0

	useEffect(() => {
		dsRepricingDrawerErrors.value = { ...dsRepricingDrawerErrors.value, seasons: isValid }
	}, [isValid])

	const shouldDisableDate = date => {
		const today = new Date()
		today.setHours(0, 0, 0, 0)
		const maxDate = new Date(today)
		maxDate.setDate(maxDate.getDate() + 730)
		return date < today || date > maxDate
	}

	const handleChange = val => {
		pickerValue.value = val
		if (!val || !val[0]) overlapError.value = null
		if (Array.isArray(val) && val[0] && val[1]) {
			// `MM-DD`, without the year. A season recurs: "high season is
			// July" is a fact about July, not about July 2026, and the
			// engine reads it that way — `sweep/period.py` even splits a
			// season that wraps the new year into two ranges. The picker
			// hands over full dates and the year used to travel with
			// them, which the API rejected outright (Curro, 2026-09-28).
			const newFrom = formatDate(parseBackendDate(val[0]), { format: 'MM-dd' })
			const newTo = formatDate(parseBackendDate(val[1]), { format: 'MM-dd' })
			const overlaps = seasons.some(s => overlapsSeason(newFrom, newTo, s))
			if (overlaps) {
				overlapError.value = "Overlaps with an existing season"
				pickerValue.value = null
				return
			}
			batch(() => {
				overlapError.value = null
				const updated = [...seasons, { seasonFrom: newFrom, seasonTo: newTo }]
				dsRepricingDrawerData.value = withPeriodField(dsRepricingDrawerData.value, 'seasons', updated)
				pickerValue.value = null
			})
		}
	}

	const remove = index => {
		const updated = seasons.filter((_, i) => i !== index)
		dsRepricingDrawerData.value = withPeriodField(dsRepricingDrawerData.value, 'seasons', updated)
	}

	return (
		<Box sx={rangeWellSx}>
			{seasons.length > 0 && (
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
					{seasons.map((s, i) => (
						<Chip key={`${s.seasonFrom}-${s.seasonTo}`}
						      label={
						          <Box component="span" sx={rangeChipFiguresSx}>
						              {s.seasonFrom}
						              <Box component="span" sx={rangeChipArrowSx}>→</Box>
						              {s.seasonTo}
						          </Box>
						      }
						      onDelete={() => remove(i)}
						      sx={rangeChipSx}/>
					))}
				</Box>
			)}
			<Box sx={{
				...rangeAddRowSx(seasons.length === 0),
				// DateRangePickerSeeker is an rsuite `DateRangePicker` wrapped in a
				// MUI FormControl. The height is decided by the rsuite input, not
				// by MUI — its inner classes are prefixed `.rs-`. Bumping them to
				// 42px lines the picker up with every other 40-42px input in the
				// wizard. `!important` because rsuite's own emotion styles have
				// higher specificity than this sx block would otherwise win.
				'& .rs-picker-toggle-wrapper, & .rs-picker-toggle, & .rs-picker-input, & .rs-input': {
					minHeight: '40px !important',
					height: '40px !important',
				},
				'& .rs-picker-toggle': {
					paddingTop: '6px !important',
					paddingBottom: '6px !important',
				},
			}}>
				<DateRangePickerSeeker
					placeholder="Add new season range"
					value={pickerValue.value}
					onChange={handleChange}
					shouldDisableDate={shouldDisableDate}/>
			</Box>
			{overlapError.value && (
				<FormHelperText error sx={{ mx: 0 }}>{overlapError.value}</FormHelperText>
			)}
			{active && seasons.length === 0 && (
				<FormHelperText error sx={{ mx: 0 }}>Add at least one season</FormHelperText>
			)}
		</Box>
	)
}
